/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import { disposableValue } from '../../../common/utils/disposable-value';
import { type rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
import { type searchResultsQuery$variables } from '../search-results/__generated__/searchResultsQuery.graphql';

/**
 * A disposable value to hold the current search session ID.
 **/
export const SEARCH_SESSION_ID: import('../../../common/utils/disposable-value').DisposableValue<string> =
	disposableValue<string>();

// DJB2 hash function for JSON objects
function hashJsonObjectDJB2(jsonObject: object): string {
	// Convert the JSON object to a string
	const jsonString = JSON.stringify(jsonObject);
	// Initialize hash value
	let hash = 5381;
	// Iterate over each character in the string
	for (let i = 0; i < jsonString.length; i++) {
		const char = jsonString.charCodeAt(i);
		hash = (hash * 33) ^ char; // XOR and multiply by 33
	}
	// Ensure the hash is a positive 32-bit integer
	return (hash >>> 0).toString(16);
}

// Types for session IDs
type SessionId = string | null | undefined;
type SearchSessionId = string | null | undefined;
type SessionIds = [SessionId, SearchSessionId];
const sessionIdsMap = new Map<string, SessionIds>();

// Retrieves or generates session IDs based on search variables
export function getSessionIds(
	variables: searchResultsQuery$variables | rightPanelQuery$variables,
): SessionIds {
	if (sessionIdsMap.size > 1000) {
		sessionIdsMap.clear();
	}
	const { sessionId, searchSessionId, ...vars } = variables;
	const queryKey = hashJsonObjectDJB2(vars);
	const existingSession = sessionIdsMap.get(queryKey);
	if (existingSession) {
		return existingSession;
	}
	sessionIdsMap.set(queryKey, [sessionId, searchSessionId]);
	return [sessionId, searchSessionId];
}

// Clears the stored session IDs
export function clearSessionIds(): void {
	sessionIdsMap.clear();
}

// Sets a unique search session ID if not already set
export const setSearchSessionId: (forceUpdate?: boolean) => void = (forceUpdate = false): void => {
	const sessionId = SEARCH_SESSION_ID.value();
	if (!sessionId || forceUpdate) {
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		SEARCH_SESSION_ID.update(uuid());
	}
};
